"use client";

import { useEffect, useRef, type ReactNode } from "react";
import {
  animate,
  motion,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  type Variants,
} from "framer-motion";

import { cn } from "@/lib/utils";

// ─── One motion language for the marketing surface ───────────────────────────
// Every widget on the landing page pulls its easing/spring from here so the
// page reads as a single system rather than a pile of independent components.

export const EASE = [0.25, 0.1, 0.25, 1] as const;
export const EASE_OUT = [0.16, 1, 0.3, 1] as const;
export const SPRING = { type: "spring", stiffness: 380, damping: 32 } as const;
export const SPRING_SOFT = {
  type: "spring",
  stiffness: 220,
  damping: 30,
} as const;

const variants: Record<string, Variants> = {
  "fade-up": {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  },
  "fade-down": {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0 },
  },
  "fade-in": {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  },
  "fade-blur": {
    hidden: { opacity: 0, y: 12, filter: "blur(6px)" },
    visible: { opacity: 1, y: 0, filter: "blur(0px)" },
  },
};

export function AnimateOnScroll({
  children,
  className,
  animation = "fade-up",
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  animation?: "fade-up" | "fade-down" | "fade-in" | "fade-blur";
  delay?: number;
}) {
  return (
    <motion.div
      className={cn(className)}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-8%" }}
      variants={variants[animation]}
      transition={{ duration: 0.55, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

// ─── Ticker ──────────────────────────────────────────────────────────────────
// Counts a number up on arrival and rolls it on every later change instead of
// snapping from the "—" placeholder. Content is written imperatively so React
// never re-renders mid-animation; children stay the constant placeholder, which
// also matches what the static (force-static) HTML ships.

export function Ticker({
  value,
  format,
  duration,
  className,
  placeholder = "—",
  countUp = true,
}: {
  value: number | null | undefined;
  format?: (v: number) => string;
  duration?: number;
  className?: string;
  placeholder?: string;
  /**
   * Whether the first value should count up from zero. Turn this off for
   * prices: fmtPrice switches precision by magnitude, so sweeping up from zero
   * flickers through "$0.0000" → "$1.00" → "$65,000". With it off the first
   * value lands instantly and only later changes roll.
   */
  countUp?: boolean;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const reduce = useReducedMotion();
  const mv = useMotionValue(0);
  const arrived = useRef(false);

  const fmt = format ?? ((v: number) => Math.round(v).toLocaleString());
  const fmtRef = useRef(fmt);
  fmtRef.current = fmt;

  useMotionValueEvent(mv, "change", (v) => {
    if (ref.current) ref.current.textContent = fmtRef.current(v);
  });

  useEffect(() => {
    if (value == null || !Number.isFinite(value)) return;
    if (reduce) {
      mv.set(value);
      if (ref.current) ref.current.textContent = fmtRef.current(value);
      arrived.current = true;
      return;
    }
    // First value counts up from zero (arrival); later values roll from the
    // previous value and roll faster — that reads as a tick, not a reveal.
    const first = !arrived.current;
    arrived.current = true;
    if (first && !countUp) {
      mv.set(value);
      if (ref.current) ref.current.textContent = fmtRef.current(value);
      return;
    }
    if (first) mv.set(0);
    const controls = animate(mv, value, {
      duration: duration ?? (first ? 0.9 : 0.45),
      ease: EASE_OUT,
      // A no-op animation (a genuine 0, or a repeat of the same value) emits
      // no "change" event, which would strand the placeholder. Write the
      // final value unconditionally.
      onComplete: () => {
        if (ref.current) ref.current.textContent = fmtRef.current(value);
      },
    });
    return () => controls.stop();
  }, [value, duration, reduce, countUp, mv]);

  return (
    <span ref={ref} className={cn("tabular-nums", className)}>
      {placeholder}
    </span>
  );
}

// ─── RadarDot ────────────────────────────────────────────────────────────────
// Replaces `animate-pulse` on live indicators. A blinking dot reads as
// "loading"; an outward ping reads as "receiving".

export function RadarDot({
  size = 8,
  dotClass = "bg-success",
  delay = 0,
  className,
}: {
  size?: number;
  dotClass?: string;
  delay?: number;
  className?: string;
}) {
  const reduce = useReducedMotion();
  return (
    <span
      className={cn("relative inline-flex flex-shrink-0", className)}
      style={{ width: size, height: size }}
    >
      {!reduce && (
        <motion.span
          aria-hidden
          className={cn("absolute inset-0 rounded-full", dotClass)}
          initial={{ opacity: 0.5, scale: 1 }}
          animate={{ opacity: 0, scale: 2.6 }}
          transition={{
            duration: 2.2,
            repeat: Infinity,
            repeatDelay: 0.3,
            ease: "easeOut",
            delay,
          }}
        />
      )}
      <span className={cn("relative h-full w-full rounded-full", dotClass)} />
    </span>
  );
}
